import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { AuthenticationError } from "types/errors/ApiErrors";

import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from "../constants";
import { CharityApiService } from "../services/CharityApiService";
import {
  CharityHousehold,
  CharityRider,
  CharitySessionStatus,
  HouseholdResponse,
  IHouseholdLogin,
  IHouseholdViewer,
  SignedInCharitySession,
} from "../types";
import { resolveCharitySessionStatus } from "../utils/charitySession";

/**
 * Charity application session and household state.
 */
interface CharityContextType {
  sessionToken: string | null;
  householdId: string | null;
  sessionStatus: CharitySessionStatus;
  hasHouseholdSession: boolean;
  signedInSession: SignedInCharitySession | null;
  setSessionToken: (token: string | null) => void;
  setHouseholdData: (household: CharityHousehold, riders: CharityRider[]) => void;
  /**
   * Replace the household wholesale from a fresh read.
   *
   * Kept apart from setHouseholdData, which the roster forms use to fold in one
   * changed rider without a round trip. Who can sign in is not derivable from a
   * rider edit, so a caller that has only riders must not be able to blank it.
   */
  setHouseholdResponse: (response: HouseholdResponse) => void;
  clearSession: () => void;
}

const CharityContext = createContext<CharityContextType | undefined>(undefined);

const readStoredValue = (storageKey: string): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem(storageKey);
};

interface CharityContextProviderProps {
  children: ReactNode;
}

/**
 * Provider for charity session and household state.
 * Manages participant session tokens, household data, and riders.
 *
 * The token outlives a reload in local storage; the household does not, so the
 * provider fetches it back whenever it holds a token without one. Until that
 * fetch answers, the session reads as restoring rather than as signed out.
 */
export const CharityContextProvider: React.FC<CharityContextProviderProps> = ({ children }) => {
  const [sessionToken, setSessionToken] = useState<string | null>(() =>
    readStoredValue(LOCAL_STORAGE_KEYS.sessionToken)
  );
  const [householdId, setHouseholdId] = useState<string | null>(() => readStoredValue(LOCAL_STORAGE_KEYS.householdId));
  const [household, setHousehold] = useState<CharityHousehold | null>(null);
  const [riders, setRiders] = useState<CharityRider[]>([]);
  const [logins, setLogins] = useState<IHouseholdLogin[]>([]);
  const [viewer, setViewer] = useState<IHouseholdViewer | null>(null);
  const [isHouseholdRestorePending, setIsHouseholdRestorePending] = useState<boolean>(
    () => readStoredValue(LOCAL_STORAGE_KEYS.sessionToken) !== null
  );

  // Memoised because consumers put these in effect dependency arrays. Rebuilt
  // every render, they re-fire those effects on any state change here.
  const handleSetSessionToken = useCallback((token: string | null) => {
    setSessionToken(token);
    setIsHouseholdRestorePending(token !== null);
    if (token) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.sessionToken, token);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.sessionToken);
    }
  }, []);

  const handleSetHouseholdData = useCallback((householdData: CharityHousehold, ridersData: CharityRider[]) => {
    setHousehold(householdData);
    setRiders(ridersData);
    setHouseholdId(householdData.id);
    setIsHouseholdRestorePending(false);
    localStorage.setItem(LOCAL_STORAGE_KEYS.householdId, householdData.id);
  }, []);

  const handleSetHouseholdResponse = useCallback(
    (response: HouseholdResponse) => {
      handleSetHouseholdData(response.household, response.riders);
      setLogins(response.logins ?? []);
      setViewer(response.viewer ?? null);
    },
    [handleSetHouseholdData]
  );

  const handleClearSession = useCallback(() => {
    setSessionToken(null);
    setHouseholdId(null);
    setHousehold(null);
    setRiders([]);
    setLogins([]);
    setViewer(null);
    setIsHouseholdRestorePending(false);
    Object.values(LOCAL_STORAGE_KEYS).forEach((storageKey) => localStorage.removeItem(storageKey));
    Object.values(SESSION_STORAGE_KEYS).forEach((storageKey) => sessionStorage.removeItem(storageKey));
  }, []);

  useEffect(() => {
    if (!isHouseholdRestorePending || sessionToken === null) {
      return;
    }

    let isRestoreCurrent = true;

    const restoreHouseholdAsync = async (): Promise<void> => {
      try {
        const householdResponse = await CharityApiService.getHouseholdAsync(sessionToken);
        if (isRestoreCurrent) {
          handleSetHouseholdResponse(householdResponse);
        }
      } catch (restoreError) {
        // A rejected token is spent — a thirty day session simply ran out — so
        // the session goes with it. Any other failure leaves the restore owed:
        // the token is probably still good, and calling a passing network fault
        // a sign-out is exactly what strands a signed-in household.
        if (isRestoreCurrent && restoreError instanceof AuthenticationError) {
          handleClearSession();
        }
      }
    };

    void restoreHouseholdAsync();

    return () => {
      isRestoreCurrent = false;
    };
  }, [isHouseholdRestorePending, sessionToken, handleSetHouseholdResponse, handleClearSession]);

  const sessionStatus = resolveCharitySessionStatus({
    sessionToken: sessionToken,
    household: household,
    isHouseholdRestorePending: isHouseholdRestorePending,
  });

  const signedInSession: SignedInCharitySession | null = useMemo(
    () =>
      sessionToken !== null && household !== null
        ? {
            sessionToken: sessionToken,
            household: household,
            riders: riders,
            logins: logins,
            ...(viewer === null ? {} : { viewer: viewer }),
          }
        : null,
    [sessionToken, household, riders, logins, viewer]
  );

  const value: CharityContextType = useMemo(
    () => ({
      sessionToken: sessionToken,
      householdId: householdId,
      sessionStatus: sessionStatus,
      hasHouseholdSession: sessionStatus !== CharitySessionStatus.SignedOut,
      signedInSession: signedInSession,
      setSessionToken: handleSetSessionToken,
      setHouseholdData: handleSetHouseholdData,
      setHouseholdResponse: handleSetHouseholdResponse,
      clearSession: handleClearSession,
    }),
    [
      sessionToken,
      householdId,
      sessionStatus,
      signedInSession,
      handleSetSessionToken,
      handleSetHouseholdData,
      handleSetHouseholdResponse,
      handleClearSession,
    ]
  );

  return <CharityContext.Provider value={value}>{children}</CharityContext.Provider>;
};

/**
 * Hook to access charity context.
 * Must be used within CharityContextProvider.
 *
 * @returns Charity context value
 * @throws Error if used outside provider
 */
export const useCharity = (): CharityContextType => {
  const context = useContext(CharityContext);
  if (context === undefined) {
    throw new Error("useCharity must be used within CharityContextProvider");
  }
  return context;
};
